import { create } from 'zustand';
import { useChatStore } from './chatStore';
import { useToastStore } from './toastStore';

type ChannelSubscription = {
  id: string;
  topic: string;
  callback: (message: any) => void;
};

interface ChannelStore {
  subscriptions: ChannelSubscription[];
  addSubscription: (subscription: ChannelSubscription) => void;
  removeSubscription: (subscription: ChannelSubscription) => void;
  handleMessage: (message: any) => void;
}

export const useChannelStore = create<ChannelStore>()((set, get) => ({
  subscriptions: [],
  addSubscription: (subscription) =>
    set((state) => ({
      subscriptions: [...state.subscriptions, subscription],
    })),
  removeSubscription: (subscription) =>
    set((state) => ({
      subscriptions: state.subscriptions.filter(
        (s) => s.id !== subscription.id,
      ),
    })),
  handleMessage: (message) => {
    // Handle chat messages specially
    if (message.type === 'chat') {
      useChatStore.getState().addMessage({
        senderID: message.userID,
        room: message.room,
        message: message.message,
      });
      return;
    }

    // Handle broadcast toasts specially (no per-topic subscription callback)
    if (message.type === 'toast') {
      useToastStore.getState().addToast({
        id: message.id,
        message: message.message,
      });
      return;
    }

    // Call callbacks for matching topic subscriptions
    const subscriptions = get().subscriptions;
    subscriptions.forEach((sub) => {
      if (sub.topic === message.topic) {
        sub.callback(message);
      }
    });
  },
}));

// Helper functions for subscribing/unsubscribing
// These are lazy-loaded to avoid circular dependency with ChannelListener
let subscriptionIdCounter = 0;

export function subscribeToTopic(
  topic: string,
  callback: (message: any) => void,
): ChannelSubscription {
  // Dynamically import to avoid circular dependency
  const { getChannelListener } = require('../ChannelListener');

  const id = `sub_${subscriptionIdCounter++}`;
  const subscription = { id, topic, callback };

  // Subscribe to the websocket topic
  getChannelListener().subscribeToTopic(topic);

  // Store the callback subscription
  useChannelStore.getState().addSubscription(subscription);

  return subscription;
}

export function unsubscribeFromTopic(subscription: ChannelSubscription): void {
  // Dynamically import to avoid circular dependency
  const { getChannelListener } = require('../ChannelListener');

  // Remove the callback subscription
  useChannelStore.getState().removeSubscription(subscription);

  // Unsubscribe from the websocket topic
  getChannelListener().unsubscribeToTopic(subscription.topic);
}
